import { Box, useToast, Text } from '@chakra-ui/react';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import configureAxios from '../axiosClient';
import { useUserStore } from '../store/user';
import { useExperiencesStore } from '../store/experiences';
import { ExperienceListItem } from '../interfaces';
import ViewerNavbar from '../components/common/ViewerNavbar';
import { loadEmperiaSDK } from '../utils/loadEmperiaSDK';

const axiosClient = configureAxios();

function Viewer() {
  const toast = useToast({
    position: 'top',
    variant: 'top-accent',
  });
  const navigate = useNavigate();
  const { experienceId } = useParams();
  const { organisation } = useUserStore();
  const {
    selectedExperience,
    setSelectedExperience,
    setSelectedExperienceScenes,
  } = useExperiencesStore();
  const [experienceLoading, setExperienceLoading] = useState<boolean>(true);
  const [isV2Experience, setIsV2Experience] = useState(false);
  const [isStandardUIReady, setIsStandardUIReady] = useState(false);

  // Event listener functions wrapped in useCallback to prevent unnecessary re-renders
  const eventListener = useCallback((event: Event) => {
    console.log('in dashboard eventListener', event);
    const interceptedEvent = event as CustomEvent;
    if (interceptedEvent.detail.name === 'experienceReady') {
      console.log('experienceReady', interceptedEvent);
      const expVer = interceptedEvent.detail.data.version;
      if (expVer.includes('Fabv2.0.0')) {
        setIsV2Experience(true);
      } else {
        setIsV2Experience(false);
      }
    }
    if (interceptedEvent.detail.name === 'uiReady') {
      console.log('uiReady on Dash');
      setIsStandardUIReady(true);
    }
  }, []);

  const fromUserInterface = useCallback(async (event: Event) => {
    const interceptedEvent = event as CustomEvent;
    if (interceptedEvent.detail.name === 'updateUIDataOnS3') {
      console.log('updateUIDataOnS3', interceptedEvent);
      try {
        const response = await axiosClient.post(
          `experience/update/data`,
          interceptedEvent.detail.data
        );
        console.log('response updateDataOnS3', response);
        if (response.data.status === 'OK') {
          window.emperia &&
            window.emperia.events.dispatchEvent(
              new CustomEvent('fromDashboard', {
                detail: {
                  name: 'DataUpdatedOnS3',
                  data: 'success',
                },
              })
            );
        }
      } catch (error) {
        window.emperia &&
          window.emperia.events.dispatchEvent(
            new CustomEvent('fromDashboard', {
              detail: {
                name: 'DataUpdatedOnS3',
                data: 'error',
              },
            })
          );
      }
    }
  }, []);

  useEffect(() => {
    const userOrgId = organisation.organizationId;

    axiosClient
      .get(`org/${userOrgId}/experiences/${experienceId}`)
      .then(async (res) => {
        if (res.data.status === 'OK') {
          const experience = res.data.data;
          setSelectedExperience(experience);

          try {
            // Load Emperia SDK dynamically first
            await loadEmperiaSDK();

            // Initialize Emperia after SDK is loaded
            window.emperia.init({
              id: 'experience-root',
              experience_url: experience.published_url
                ? experience.published_url.replace('index', 'experience')
                : experience.preview_url?.replace('index', 'experience'),
              ui_url: process.env.REACT_APP_STANDARD_UI_URL as string,
              attach_ui: true,
              debug: false,
              organization_id: '587accae-22f4-41e4-86c6-fa83d20e7f65',
            });

            // Attach event listeners after emperia is initialized
            const target = window?.emperia?.events || window;
            console.log('listeners added after emperia init');
            target.addEventListener('fromExperience', eventListener);
            target.addEventListener('fromUserInterface', fromUserInterface);

            // Watch for ui-root element creation and add ql-editor class
            const observer = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                  mutation.addedNodes.forEach((node) => {
                    if (node instanceof Element && node.id === 'ui-root') {
                      node.classList.add('ql-editor');
                      observer.disconnect(); // Stop observing once we've added the class
                    }
                  });
                }
              });
            });

            // Start observing the document body for the ui-root element
            observer.observe(document.body, {
              childList: true,
              subtree: true,
            });

            setExperienceLoading(false);
          } catch (error) {
            console.error('Failed to load Emperia SDK:', error);
            navigate('/');
            if (!toast.isActive('sdkError')) {
              toast({
                id: 'sdkError',
                title: 'Failed to load experience viewer',
                status: 'error',
              });
            }
          }
        } else {
          navigate('/');
          if (!toast.isActive('experienceErrorOne')) {
            toast({
              id: 'experienceErrorOne',
              title: `Experience does not exist`,
              status: 'info',
            });
          }
        }
      })
      .catch((err: unknown) => {
        console.log(err);
        navigate('/');
        if (!toast.isActive('experienceErrorTwo')) {
          toast({
            id: 'experienceErrorTwo',
            title: `Experience does not exist`,
            status: 'info',
          });
        }
      });

    return () => {
      // Cleanup event listeners when component unmounts or effect re-runs
      const target = window?.emperia?.events || window;
      target.removeEventListener('fromExperience', eventListener);
      target.removeEventListener('fromUserInterface', fromUserInterface);

      const scriptToRemove = document.getElementById('standard-ui-script');
      console.log('scriptToRemove', scriptToRemove);
      if (scriptToRemove && scriptToRemove.parentNode) {
        scriptToRemove.remove();
      }
      const linkToRemove = document.getElementById('standard-ui-link');
      console.log('linkToRemove', linkToRemove);
      if (linkToRemove && linkToRemove.parentNode) {
        linkToRemove.remove();
      }
      const uiElementToRemove = document.getElementById('ui-root');
      if (uiElementToRemove) {
        uiElementToRemove.remove();
      }
    };
  }, [eventListener, fromUserInterface]);

  useEffect(() => {
    selectedExperience?.id?.length > 0 &&
      axiosClient
        .get(`/experience/${selectedExperience.id}/scenes`)
        .then((res) => {
          if (res.data.status === 'OK') {
            setSelectedExperienceScenes(
              res.data.data?.sort(
                (a: ExperienceListItem, b: ExperienceListItem) =>
                  a.created &&
                  b.created &&
                  new Date(a.created).getTime() - new Date(b.created).getTime()
              )
            );
          }
        })
        .catch((err: unknown) => {
          console.log(err);
        });
  }, [selectedExperience]);

  return (
    <Box>
      <ViewerNavbar
        isV2Experience={isV2Experience}
        isStandardUIReady={isStandardUIReady}
      />
      <Box
        w='100%'
        h='calc(100vh - 65px)'
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        position='fixed'
        bg='#08101D'
        color='white'
      >
        <Box
          height='100%'
          width='100%'
          position='absolute'
          top='0px'
          left='0px'
          zIndex='1'
          bg='#08101D'
          opacity={experienceLoading ? 1 : 0}
          visibility={experienceLoading ? 'visible' : 'hidden'}
          transition='all 0.2s'
          display='flex'
          justifyContent='center'
          alignItems='center'
        >
          <Text fontSize='20px'>Loading your experience</Text>
        </Box>
        <Box
          id='experience-root'
          height='100%'
          minHeight='calc(100vh - 65px)'
          width='100%'
        />
      </Box>
    </Box>
  );
}

export default Viewer;
